import {create, ifjs, signalMap} from "../../fjsc/src/f2.ts";
import {compute, signal} from "../../fjsc/src/signals.ts";
import {Generics} from "./generics.ts";

export interface BingoCell {
    value: string;
    active: boolean;
}

export class Bingo {
    static bingo(data: string[]) {
        const randomizedData = data.sort(() => Math.random() - 0.5);
        const mappedData = Bingo.mapToBingoRows(randomizedData);
        const bingoData = signal<BingoCell[][]>(mappedData.map(row => {
            return row.map(value => {
                return {
                    value,
                    active: false
                };
            });
        }));
        const winningCells = compute(b => Bingo.hasBingo(b), bingoData);

        return create("table")
            .classes("bingo")
            .children(
                signalMap(bingoData, create("tbody"), (row, y) => {
                    return create("tr")
                        .children(
                            ...row.map((cell, x) => {
                                const active = (cell.active || cell.value === "*") ? "active" : "_";
                                const isWinning = compute(w => {
                                    return w?.some(c => c.y === y && c.x === x) ?? false;
                                }, winningCells);
                                const winningClass = compute(w => w ? "winning" : "_", isWinning);
                                const showGif = compute(w => {
                                    return w && cell.value === "*";
                                }, winningCells);

                                const onclick = () => {
                                    if (cell.value === "*") {
                                        return;
                                    }
                                    bingoData.value = bingoData.value.map(row => {
                                        return row.map(c => {
                                            if (c.value === cell.value) {
                                                c.active = !cell.active;
                                            }
                                            return c;
                                        });
                                    });
                                }

                                return create("td")
                                    .classes("bingo-cell", active, winningClass)
                                    .onclick(onclick)
                                    .children(
                                        ifjs(showGif, create("div")
                                            .classes("bingo-cell-inner-value")
                                            .text(cell.value)
                                            .build(), true),
                                        ifjs(showGif, Generics.image("/img/cat.gif", ["bingo-gif"]))
                                    ).build();
                            })
                        ).build();
                })
            ).build();
    }

    static mapToBingoRows(data: string[]) {
        let out = [];
        const rows = 5;
        const cols = 5;
        let hadMiddle = false;
        for (let y = 0; y < rows; y++) {
            let row = [];
            for (let x = 0; x < cols; x++) {
                if (y === Math.floor(rows / 2) && x === Math.floor(cols / 2)) {
                    row.push("*");
                    hadMiddle = true;
                } else {
                    row.push(data[y * cols + x - (hadMiddle ? 1 : 0)]);
                }
            }
            out.push(row);
        }
        return out;
    }

    private static hasBingo(b: BingoCell[][]): { y: number; x: number }[] | null {
        const rows = b.length;
        const cols = b[0].length;

        // Function to check if all cells in a line are "active"
        const isWinningLine = (cells: { y: number; x: number }[]): boolean =>
            cells.every(({ y, x }) => b[y][x].active || b[y][x].value === "*");

        // Check horizontal lines
        for (let y = 0; y < rows; y++) {
            const horizontalLine = Array.from({ length: cols }, (_, x) => ({ y, x }));
            if (isWinningLine(horizontalLine)) {
                return horizontalLine;
            }
        }

        // Check vertical lines
        for (let x = 0; x < cols; x++) {
            const verticalLine = Array.from({ length: rows }, (_, y) => ({ y, x }));
            if (isWinningLine(verticalLine)) {
                return verticalLine;
            }
        }

        // Check the main diagonal (top-left to bottom-right)
        const mainDiagonal = Array.from({ length: rows }, (_, i) => ({ y: i, x: i }));
        if (isWinningLine(mainDiagonal)) {
            return mainDiagonal;
        }

        // Check the anti-diagonal (top-right to bottom-left)
        const antiDiagonal = Array.from({ length: rows }, (_, i) => ({ y: i, x: cols - 1 - i }));
        if (isWinningLine(antiDiagonal)) {
            return antiDiagonal;
        }

        // If no bingo is found, return null
        return null;
    }
}